import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import type { AutoTopup, CreditPricing } from "api";
import cn from "classnames";
import { formatCount } from "utils/number.utils";
import type { BuyMode } from "./credits.model";
import {
  buyCopy,
  clampCredits,
  CONTACT_THRESHOLD,
  DEFAULT_CREDITS,
  discountPercent,
  formatRate,
  formatUsd,
  QUOTE_URL,
  rateFor,
  SLIDER_MAX,
  SLIDER_TOP,
  toCredits,
  toPosition,
  totalUsd,
} from "./credits.model";

type BuyCardProps = {
  pricing: CreditPricing;
  /** The live auto top-up, if any: it hides the checkbox and prices `change`. */
  auto: AutoTopup | null;
  mode: BuyMode;
  busy: boolean;
  /** Back to `once`. Only offered in `extra` and `change`. */
  onClose: () => void;
  onSubmit: (credits: number, autoTopup: boolean) => void;
};

/**
 * `#acr-buy`: the QCredits calculator. A slider for the common amounts, an
 * "Any amount" field past its end, and the packs the back office lists. Every
 * figure comes from `/api/credits/pricing`, the same tiers Checkout charges.
 *
 * - `once`: buy, optionally as a monthly auto top-up;
 * - `extra`: a one-off purchase next to a running auto top-up;
 * - `change`: the auto top-up's new amount, ticked and locked on.
 */
export const BuyCard = ({ pricing, auto, mode, busy, onClose, onSubmit }: BuyCardProps) => {
  const startCredits = mode === "change" && auto !== null ? auto.credits : DEFAULT_CREDITS;
  const [credits, setCredits] = useState(() => clampCredits(startCredits, pricing));
  const [position, setPosition] = useState(() => Math.min(SLIDER_MAX - 1, Math.round(toPosition(startCredits))));
  const [typed, setTyped] = useState(String(startCredits));
  const [editing, setEditing] = useState(false);
  const [autoTopup, setAutoTopup] = useState(false);
  const numberRef = useRef<HTMLInputElement>(null);

  // A new mode starts from its own amount: the running auto top-up's for `change`.
  useEffect(() => {
    const next = clampCredits(mode === "change" && auto !== null ? auto.credits : DEFAULT_CREDITS, pricing);

    setCredits(next);
    setPosition(next >= SLIDER_TOP ? SLIDER_MAX : Math.min(SLIDER_MAX - 1, Math.round(toPosition(next))));
    setTyped(String(next));
  }, [mode, auto, pricing]);

  const set = (value: number, from: "slider" | "field" | "pack") => {
    const next = clampCredits(value, pricing);

    setCredits(next);
    if (from !== "slider") {
      setPosition(next >= SLIDER_TOP ? SLIDER_MAX : Math.min(SLIDER_MAX - 1, Math.round(toPosition(next))));
    }
    if (from !== "field") setTyped(String(next));
  };

  const copy = buyCopy(mode, auto);
  const rate = rateFor(pricing, credits);
  const discount = discountPercent(pricing, credits);
  const atMax = credits >= SLIDER_TOP || editing;
  const fill = position / SLIDER_MAX;
  // An auto top-up already running owns the monthly checkbox; `change` shows it locked on.
  const showAuto = auto === null || mode === "change";
  const asAutoTopup = mode === "change" || (auto === null && autoTopup);

  return (
    <div className="pcx" id="acr-buy">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">{copy.title}</div>
          <p className="pcx__sub">{copy.sub}</p>
        </div>
        {mode !== "once" && (
          <button type="button" className="pcbtn acr__x" onClick={onClose}>
            Cancel
          </button>
        )}
      </div>

      <div className="prcr__body">
        <div className="prcr__pick">
          <div className="prcr__row">
            <div className="prcr__rng" style={{ "--p": `${fill * 100}%`, "--pn": fill } as CSSProperties}>
              <div className={cn("prcr__bub", { "is-max": atMax })}>
                <span className="prcr__bv">{formatCount(credits)}</span>
                <label className="prcr__bx">
                  <span>Any amount</span>
                  <input
                    ref={numberRef}
                    type="number"
                    min={pricing.min_credits}
                    max={pricing.max_credits}
                    step={1}
                    inputMode="numeric"
                    aria-label="QCredits"
                    value={typed}
                    onFocus={() => setEditing(true)}
                    onChange={(event) => {
                      setTyped(event.target.value);
                      if (event.target.value !== "") set(Number(event.target.value), "field");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                    onBlur={() => {
                      setEditing(false);
                      set(typed === "" ? credits : Number(typed), "pack");
                    }}
                  />
                </label>
              </div>
              <input
                className="prcr__in"
                type="range"
                min={0}
                max={SLIDER_MAX}
                step={1}
                aria-label="Number of QCredits"
                value={position}
                onChange={(event) => {
                  const next = Number(event.target.value);

                  setPosition(next);
                  set(toCredits(next), "slider");
                }}
                onPointerUp={() => {
                  // Released at the end: the field takes over for larger amounts.
                  if (position >= SLIDER_MAX) {
                    numberRef.current?.focus();
                    numberRef.current?.select();
                  }
                }}
              />
              <div className="prcr__marks" aria-hidden="true">
                <span style={{ left: "0%" }}>{pricing.min_credits}</span>
                <span style={{ left: "100%" }}>{formatCount(SLIDER_TOP)}</span>
              </div>
            </div>
          </div>

          {pricing.packs.length > 0 && (
            <div className="prcr__pks">
              {pricing.packs
                .filter((pack) => pack.credits < SLIDER_TOP)
                .map((pack) => (
                  <button
                    key={pack.credits}
                    type="button"
                    className={cn("prcr__pk", { "is-on": pack.credits === credits })}
                    onClick={() => set(pack.credits, "pack")}
                  >
                    {pack.label !== null && <em>{pack.label}</em>}
                    <b>{formatCount(pack.credits)} QCredits</b>
                    <span>{formatUsd(pack.total_usd)}</span>
                  </button>
                ))}
            </div>
          )}
        </div>

        <div className="prcr__sum">
          <dl className="prcr__dl">
            <div>
              <dt>Per QCredit</dt>
              <dd>
                {discount !== null && <span className="prcr__off">−{discount}%</span>}
                <span>{formatRate(rate)}</span>
              </dd>
            </div>
            <div>
              <dt>Total QCredits</dt>
              <dd>
                <b>{formatCount(credits)}</b>
              </dd>
            </div>
            <div className="prcr__tot">
              <dt>{mode === "change" ? "Monthly price" : "Total price"}</dt>
              <dd>{formatUsd(totalUsd(pricing, credits))}</dd>
            </div>
          </dl>

          {showAuto && (
            <label className={cn("prcr__auto", { "is-locked": mode === "change" })}>
              <input
                type="checkbox"
                checked={asAutoTopup}
                disabled={mode === "change"}
                onChange={(event) => setAutoTopup(event.target.checked)}
              />
              <i aria-hidden="true" />
              <span>
                Auto top-up <b>{formatCount(credits)}</b> QCredits monthly
              </span>
            </label>
          )}

          <button
            type="button"
            className="pcbtn pcbtn--main prcr__cta"
            disabled={busy || (mode === "change" && auto !== null && credits === auto.credits)}
            onClick={() => onSubmit(credits, asAutoTopup)}
          >
            {busy ? (mode === "change" ? "Saving…" : "Opening…") : copy.cta}
          </button>
        </div>
      </div>

      <p className="acr__more">
        Using more than {formatCount(CONTACT_THRESHOLD)} QCredits a month?{" "}
        <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
          Contact us
        </a>{" "}
        for a special offer.
      </p>
    </div>
  );
};
